import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

/**
 * TaskInbox — shows pending manual outreach tasks for the current recruiter.
 * Can be embedded in a page or used standalone.
 */
const TaskInbox = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [scope, setScope] = useState('firm'); // firm | mine — default firm so all tasks show
  const [completing, setCompleting] = useState(null);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/outreach/tasks?status=${filter}&scope=${scope}`, { headers });
      if (res.ok) setTasks(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadTasks(); }, [filter, scope]);

  const completeTask = async (taskId) => {
    setCompleting(taskId);
    try {
      const res = await fetch(`${API}/api/outreach/tasks/${taskId}/complete`, { method: 'PUT', headers });
      if (res.ok) {
        setTasks(prev => prev.filter(t => t.id !== taskId));
      }
    } catch (e) { console.error(e); }
    finally { setCompleting(null); }
  };

  const pendingCount = tasks.length;

  if (embedded) {
    // Compact version for embedding in header/sidebar
    return (
      <div>
        {loading ? (
          <div className="text-xs text-gray-400 py-2 text-center">Loading...</div>
        ) : tasks.length === 0 ? (
          <div className="text-xs text-gray-400 py-4 text-center">No pending tasks</div>
        ) : (
          <div className="space-y-1.5 max-h-64 overflow-auto">
            {tasks.slice(0, 10).map(task => (
              <div key={task.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                <div className="min-w-0 flex-1 mr-2">
                  <div className="text-xs font-medium text-gray-900 truncate">{task.title}</div>
                  <div className="text-[10px] text-gray-400 truncate">
                    {task.candidate_name} · {task.campaign_name}
                  </div>
                </div>
                <button onClick={() => completeTask(task.id)} disabled={completing === task.id}
                  className="px-2 py-0.5 text-[10px] bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50 flex-shrink-0">
                  {completing === task.id ? '...' : 'Done'}
                </button>
              </div>
            ))}
            {tasks.length > 10 && (
              <button onClick={() => navigate('/outreach?tab=tasks')} className="w-full text-center text-[10px] text-blue-500 py-1 hover:underline">
                View all {tasks.length} tasks
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full page version
  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--list-page-bg)' }}>
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 flex-shrink-0">
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-md p-0.5">
          {['pending', 'completed'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${filter === f ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {f === 'pending' ? 'Pending' : 'Completed'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-md p-0.5">
          {[['firm', 'All'], ['mine', 'Mine']].map(([s, label]) => (
            <button key={s} onClick={() => setScope(s)}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${scope === s ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <span className="text-[11px] text-gray-400 tabular-nums">{tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm">{filter === 'pending' ? 'No pending tasks' : 'No completed tasks'}</p>
          <p className="text-xs mt-1">Manual outreach tasks appear here when a campaign reaches a task step</p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 mx-3 mb-8 rounded-lg border shadow-sm flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--list-block-bg)' }}>
          <table className="w-full text-sm">
            <thead className="flex-shrink-0" style={{ backgroundColor: 'var(--list-header-bg)', color: 'var(--list-header-text)' }}>
              <tr>
                <th className="text-left pl-4 pr-3 py-2 text-[10px] font-medium uppercase tracking-wide rounded-tl-lg" style={{ borderBottom: '1px solid var(--list-header-border)' }}>Task</th>
                <th className="text-left px-3 py-2 text-[10px] font-medium uppercase tracking-wide" style={{ borderBottom: '1px solid var(--list-header-border)' }}>Candidate</th>
                <th className="text-left px-3 py-2 text-[10px] font-medium uppercase tracking-wide" style={{ borderBottom: '1px solid var(--list-header-border)' }}>Campaign</th>
                <th className="text-left px-3 py-2 text-[10px] font-medium uppercase tracking-wide" style={{ borderBottom: '1px solid var(--list-header-border)' }}>Status</th>
                <th className="px-2 py-2 w-28 rounded-tr-lg" style={{ borderBottom: '1px solid var(--list-header-border)' }}></th>
              </tr>
            </thead>
            <tbody className="flex-1 overflow-y-auto">
              {tasks.map(task => (
                <tr key={task.id} className="border-b last:border-b-0 transition-colors group bg-white/60 hover:bg-emerald-50/50">
                  <td className="pl-4 pr-3 py-2.5">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">{task.title}</div>
                      {task.description && <div className="text-[11px] text-gray-400 truncate max-w-md">{task.description}</div>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      {task.candidate_photo ? (
                        <img src={task.candidate_photo} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {task.candidate_name?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">{task.candidate_name}</div>
                        {task.candidate_company && <div className="text-[11px] text-gray-500 truncate">{task.candidate_company}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="text-gray-600 truncate">{task.campaign_name}</div>
                    <div className="text-[10px] text-gray-400">
                      {new Date(task.created_at).toLocaleDateString()}
                      {task.completed_at && ` · done ${new Date(task.completed_at).toLocaleDateString()}`}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${task.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                      {task.status}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 text-right">
                    {task.status === 'pending' && (
                      <button onClick={() => completeTask(task.id)} disabled={completing === task.id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 shadow-sm">
                        {completing === task.id ? 'Saving...' : (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                            Complete
                          </>
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TaskInbox;
